import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { API_SERVER_URL } from 'js/constants/api';
import { getAccessToken } from 'js/reducers/session/selectors';
import {
  activateOrgMembersMutationDefinition,
  activateOrgMutationDefinition,
  createOrgMutationDefinition,
  deactivateOrgMembersMutationDefinition,
  deactivateOrgMutationDefinition,
  getOrgAccountMetricsQueryDefinition,
  getOrgAccountSummaryQueryDefinition,
  getOrgMembersQueryDefinition,
  getOrgQueryDefinition,
  getOrgsQueryDefinition,
  getOrgTiersQueryDefinition,
  updateOrgMembersMutationDefinition,
  updateOrgMutationDefinition,
  updateOrgTierMutationDefinition,
} from './adminOrgs.endpoints';
import type {
  AdminOrg,
  CreateOrgPayload,
  OrgAccountMetrics,
  OrgAccountMetricsParams,
  OrgAccountSummary,
  OrgMember,
  OrgTier,
  UpdateOrgArg,
  UpdateOrgMembersArg,
  UpdateOrgTierArg,
} from './adminOrgs.types';

/**
 * Shape read by `getAccessToken` (see js/reducers/session/selectors.js).
 * We do not use `ReturnType<typeof rootReducer>` here: js/reducers imports this API slice,
 * so importing the combined reducer would create a circular module dependency.
 */
type ReduxStateForAccessToken = {
  session: Record<string, unknown>;
  entities: {
    users?: Record<string, unknown>;
    orgs?: Record<string, unknown>;
  };
};

const TAG_ORG = 'AdminOrg' as const;
const TAG_ORG_LIST = 'AdminOrgList' as const;
const TAG_ORG_MEMBERS = 'AdminOrgMembers' as const;
const TAG_ORG_TIER_LIST = 'AdminOrgTierList' as const;
const TAG_TYPES = [TAG_ORG, TAG_ORG_LIST, TAG_ORG_MEMBERS, TAG_ORG_TIER_LIST] as const;

export const adminOrgsApi = createApi({
  reducerPath: 'adminOrgsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_SERVER_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getAccessToken(getState() as ReduxStateForAccessToken);
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  keepUnusedDataFor: 60,
  tagTypes: TAG_TYPES,
  endpoints: (builder) => ({
    getAdminOrgs: builder.query<AdminOrg[], void>({
      query: getOrgsQueryDefinition,
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: TAG_ORG, id })), TAG_ORG_LIST]
          : [TAG_ORG_LIST],
    }),

    getAdminOrg: builder.query<AdminOrg, number>({
      query: (id) => getOrgQueryDefinition(id),
      providesTags: (_result, _err, id) => [{ type: TAG_ORG, id }],
    }),

    updateAdminOrg: builder.mutation<AdminOrg, UpdateOrgArg>({
      query: (arg) => updateOrgMutationDefinition(arg),
      invalidatesTags: (_result, _err, arg) => [{ type: TAG_ORG, id: arg.id }, TAG_ORG_LIST],
    }),

    activateAdminOrg: builder.mutation<AdminOrg, number>({
      query: (id) => activateOrgMutationDefinition(id),
      invalidatesTags: (_result, _err, id) => [{ type: TAG_ORG, id }, TAG_ORG_LIST],
    }),

    deactivateAdminOrg: builder.mutation<AdminOrg, number>({
      query: (id) => deactivateOrgMutationDefinition(id),
      invalidatesTags: (_result, _err, id) => [{ type: TAG_ORG, id }, TAG_ORG_LIST],
    }),

    getAdminOrgTiers: builder.query<OrgTier[], void>({
      query: getOrgTiersQueryDefinition,
      providesTags: [TAG_ORG_TIER_LIST],
    }),

    updateAdminOrgTier: builder.mutation<OrgTier, UpdateOrgTierArg>({
      query: (arg) => updateOrgTierMutationDefinition(arg),
      // A tier's limits apply to every org on that tier, not just the one open in the
      // drawer — we don't know which org ids are affected, so invalidate the whole TAG_ORG
      // type (bare, no id) rather than just TAG_ORG_LIST, or the currently-open org's
      // single-fetch cache (tagged {TAG_ORG, id}) never refetches and its tier data goes stale.
      invalidatesTags: [TAG_ORG_TIER_LIST, TAG_ORG_LIST, TAG_ORG],
    }),

    getAdminOrgMembers: builder.query<OrgMember[], number>({
      query: (orgId) => getOrgMembersQueryDefinition(orgId),
      providesTags: (_result, _err, orgId) => [{ type: TAG_ORG_MEMBERS, id: orgId }],
    }),

    updateAdminOrgMembers: builder.mutation<OrgMember[], UpdateOrgMembersArg>({
      query: (arg) => updateOrgMembersMutationDefinition(arg),
      invalidatesTags: (_result, _err, arg) => [
        { type: TAG_ORG_MEMBERS, id: arg.orgId },
        { type: TAG_ORG, id: arg.orgId },
      ],
    }),

    activateAdminOrgMembers: builder.mutation<OrgMember[], UpdateOrgMembersArg>({
      query: (arg) => activateOrgMembersMutationDefinition(arg),
      invalidatesTags: (_result, _err, arg) => [{ type: TAG_ORG_MEMBERS, id: arg.orgId }],
    }),

    deactivateAdminOrgMembers: builder.mutation<OrgMember[], UpdateOrgMembersArg>({
      query: (arg) => deactivateOrgMembersMutationDefinition(arg),
      invalidatesTags: (_result, _err, arg) => [{ type: TAG_ORG_MEMBERS, id: arg.orgId }],
    }),

    createAdminOrg: builder.mutation<AdminOrg, CreateOrgPayload>({
      query: (body) => createOrgMutationDefinition(body),
      invalidatesTags: [TAG_ORG_LIST],
    }),

    getAdminOrgAccountMetrics: builder.query<OrgAccountMetrics, OrgAccountMetricsParams>({
      query: (params) => getOrgAccountMetricsQueryDefinition(params),
    }),

    getAdminOrgAccountSummary: builder.query<OrgAccountSummary, number>({
      query: (orgId) => getOrgAccountSummaryQueryDefinition(orgId),
    }),
  }),
});

/**
 * Note For Dev:
 * - Export here what you need to consume, treat these as the source of truth for the adminOrgs API.
 * - Do not import `adminOrgsApi` in feature code, use the exports instead.
 */

export const resetAdminOrgsApiState = adminOrgsApi.util.resetApiState;

export const invalidateAdminOrgListTags = () => adminOrgsApi.util.invalidateTags([TAG_ORG_LIST]);

export const useFetchAdminOrgs = adminOrgsApi.useGetAdminOrgsQuery;
export const useFetchAdminOrg = adminOrgsApi.useGetAdminOrgQuery;
export const useUpdateAdminOrg = adminOrgsApi.useUpdateAdminOrgMutation;
export const useActivateAdminOrg = adminOrgsApi.useActivateAdminOrgMutation;
export const useDeactivateAdminOrg = adminOrgsApi.useDeactivateAdminOrgMutation;
export const useFetchAdminOrgTiers = adminOrgsApi.useGetAdminOrgTiersQuery;
export const useUpdateAdminOrgTier = adminOrgsApi.useUpdateAdminOrgTierMutation;
export const useFetchAdminOrgMembers = adminOrgsApi.useGetAdminOrgMembersQuery;
export const useUpdateAdminOrgMembers = adminOrgsApi.useUpdateAdminOrgMembersMutation;
export const useActivateAdminOrgMembers = adminOrgsApi.useActivateAdminOrgMembersMutation;
export const useDeactivateAdminOrgMembers = adminOrgsApi.useDeactivateAdminOrgMembersMutation;
export const useCreateAdminOrg = adminOrgsApi.useCreateAdminOrgMutation;
export const useFetchAdminOrgAccountMetrics = adminOrgsApi.useGetAdminOrgAccountMetricsQuery;
export const useFetchAdminOrgAccountSummary = adminOrgsApi.useGetAdminOrgAccountSummaryQuery;
